import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Field, Form, FormRenderProps } from 'react-final-form'

import { Button, Column, InputField, RadioField, Text } from 'Components/UI'
import validate from 'validate.js'

import { TicketKindId } from 'Constants/Ids'
import { PHONE_REGEX } from 'Constants/Regex'

import { useAppContext } from 'Services/AppContext'

import Counter from './Counter'
import { StyledForm, TicketTypeContainer } from './styles'

enum FieldName {
  FirstName = 'firstName',
  LastName = 'lastName',
  Phone = 'phoneNumber',
  CompanyName = 'companyName',
  TicketKind = 'ticketKind',
  TicketAmount = 'ticketAmount',
}

export type FormValues = {
  [FieldName.FirstName]: string
  [FieldName.LastName]: string
  [FieldName.Phone]: string
  [FieldName.CompanyName]: string
  [FieldName.TicketKind]: string
  [FieldName.TicketAmount]: number
}

const CONSTRAINTS = {
  [FieldName.FirstName]: {
    presence: {
      allowEmpty: false,
      message: `can't be blank`,
    },
  },
  [FieldName.LastName]: {
    presence: {
      allowEmpty: false,
      message: `can't be blank`,
    },
  },
  [FieldName.Phone]: {
    presence: {
      allowEmpty: false,
      message: `can't be blank`,
    },
    format: {
      pattern: new RegExp(PHONE_REGEX),
      message: 'is not a valid',
    },
  },
  [FieldName.CompanyName]: {
    presence: {
      allowEmpty: false,
      message: `can't be blank`,
    },
  },
}

type Props = {
  disabled?: boolean
  loading?: boolean
  onSubmit?: (value: FormValues) => void
}

function SubmitToEventForm({ disabled, loading, onSubmit }: Props) {
  const { me } = useAppContext()
  const frozenMeRef = useRef<typeof me | null>(me)

  useEffect(() => {
    if (!disabled) {
      frozenMeRef.current = me
    }
  }, [disabled, me])

  const frozenMe = frozenMeRef.current

  const initialValues = useMemo(
    () => ({
      [FieldName.FirstName]: frozenMe?.profile?.firstName,
      [FieldName.LastName]: frozenMe?.profile?.lastName,
      [FieldName.Phone]: frozenMe?.profile?.phoneNumber,
      [FieldName.CompanyName]: frozenMe?.meta?.value?.companyName,
      [FieldName.TicketKind]: TicketKindId.GolfPlus,
      [FieldName.TicketAmount]: 1,
    }),
    [frozenMe],
  )

  const formValidation = useCallback(
    (values: FormValues) => validate(values, CONSTRAINTS),
    [],
  )

  const renderForm = useCallback(
    ({ handleSubmit, submitting, values }: FormRenderProps<FormValues>) => (
      <StyledForm onSubmit={handleSubmit}>
        {!frozenMe?.profile?.firstName && !loading && (
          <InputField
            displayError
            label="First name"
            mb={7}
            name={FieldName.FirstName}
          />
        )}

        {!frozenMe?.profile?.lastName && !loading && (
          <InputField
            displayError
            label="Last name"
            mb={7}
            name={FieldName.LastName}
          />
        )}

        {!frozenMe?.profile?.phoneNumber && !loading && (
          <InputField
            displayError
            label="Phone number"
            mb={7}
            name={FieldName.Phone}
          />
        )}

        {!frozenMe?.meta?.value?.companyName && !loading && (
          <InputField
            displayError
            label="Company"
            mb={7}
            name={FieldName.CompanyName}
          />
        )}

        <Column gap={3}>
          <Text caption secondary>
            Ticket type
          </Text>

          <TicketTypeContainer>
            <RadioField
              label="Golf + Networking"
              name={FieldName.TicketKind}
              value={TicketKindId.GolfPlus}
            />

            <Field name={FieldName.TicketAmount}>
              {({ input: { value, onChange: handleChange } }) => (
                <Counter
                  disabled={
                    values[FieldName.TicketKind] !== TicketKindId.GolfPlus
                  }
                  value={value}
                  onChange={value => handleChange(value)}
                />
              )}
            </Field>
          </TicketTypeContainer>

          <TicketTypeContainer>
            <RadioField
              label="Clinic + Networking"
              name={FieldName.TicketKind}
              value={TicketKindId.ClinicPlus}
            />

            <Field name={FieldName.TicketAmount}>
              {({ input: { value, onChange: handleChange } }) => (
                <Counter
                  disabled={
                    values[FieldName.TicketKind] !== TicketKindId.ClinicPlus
                  }
                  value={value}
                  onChange={value => handleChange(value)}
                />
              )}
            </Field>
          </TicketTypeContainer>

          <TicketTypeContainer>
            <RadioField
              label="Networking only"
              name={FieldName.TicketKind}
              value={TicketKindId.Networking}
            />

            <Field name={FieldName.TicketAmount}>
              {({ input: { value, onChange: handleChange } }) => (
                <Counter
                  disabled={
                    values[FieldName.TicketKind] !== TicketKindId.Networking
                  }
                  value={value}
                  onChange={value => handleChange(value)}
                />
              )}
            </Field>
          </TicketTypeContainer>
        </Column>

        <Button
          disabled={submitting || loading || disabled}
          large
          mt={9}
          type="submit"
          width={1}
        >
          Submit
        </Button>
      </StyledForm>
    ),
    [loading, frozenMe, disabled],
  )

  const handleFormSubmit = useCallback(
    (values: FormValues) => {
      onSubmit?.(values)
    },
    [onSubmit],
  )

  return (
    <Form
      initialValues={initialValues}
      render={renderForm}
      validate={formValidation}
      onSubmit={handleFormSubmit}
    />
  )
}

export default SubmitToEventForm
